// src/components/DemoAnalytics.js
import React, { useState, useEffect, useMemo } from "react";
import { Doughnut, Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import { useNavigate, useLocation } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import { getCompanyData } from "../../Service/apiService";
import "../../assets/css/Analytics.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css"; 

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend
);

const toLower = (val) => (val || "").toLowerCase().trim();

const parseCountField = (val) => {
  if (val == null) return 0;
  if (typeof val === "string" && val.startsWith(">")) return 6;
  return parseInt(val) || 0;
};

export default function DemoAnalytics() {
  const navigate = useNavigate();
  const location = useLocation();

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [demoTimeRange, setDemoTimeRange] = useState("6m");

  //  Custom date state
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getCompanyData("all");
        setCompanies(res?.data || []);
      } catch (err) {
        console.error("Error fetching company data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const nonInternalCompanies = useMemo(() => {
    return companies.filter((c) => toLower(c.companyType) !== "internal");
  }, [companies]);

  // === 1. Geography Distribution (Donut) ===
  const geographyData = useMemo(() => {
    const india = nonInternalCompanies
      .filter((c) => toLower(c.country) === "india")
      .reduce((sum, c) => sum + parseCountField(c.demo), 0);

    const foreign = nonInternalCompanies
      .filter((c) => toLower(c.country) !== "india")
      .reduce((sum, c) => sum + parseCountField(c.demo), 0);

    return {
      labels: ["India", "Foreign"],
      datasets: [
        {
          data: [india, foreign],
          backgroundColor: [
            "rgba(5, 55, 99, 0.85)",
            "rgba(66, 111, 155, 0.85)",
          ],
          borderColor: "#fff",
          borderWidth: 2,
        },
      ],
    };
  }, [nonInternalCompanies]);

  // === 2. Demos vs. Meetings by Company Type (Grouped Bar) ===
  const companyTypes = ["New", "Renewal", "Lost"];
  const segmentData = useMemo(() => {
    const demoData = companyTypes.map((type) => {
      const comps = nonInternalCompanies.filter(
        (c) => toLower(c.companyType) === toLower(type)
      );
      if (comps.length === 0) return 0;
      const total = comps.reduce((sum, c) => sum + parseCountField(c.demo), 0);
      return Math.round((total / comps.length) * 10) / 10;
    });

    const meetingData = companyTypes.map((type) => {
      const comps = nonInternalCompanies.filter(
        (c) => toLower(c.companyType) === toLower(type)
      );
      if (comps.length === 0) return 0;
      const total = comps.reduce(
        (sum, c) => sum + parseCountField(c.personalMeeting),
        0
      );
      return Math.round((total / comps.length) * 10) / 10;
    });

    return {
      labels: companyTypes,
      datasets: [
        {
          label: "Avg Demos",
          data: demoData,
          backgroundColor: "rgba(5, 55, 99, 0.8)",
          borderColor: "rgba(5, 55, 99, 1)",
          borderWidth: 1,
        },
        {
          label: "Avg Meetings",
          data: meetingData,
          backgroundColor: "rgba(147, 193, 239, 0.8)",
          borderColor: "rgba(147, 193, 239, 1)",
          borderWidth: 1,
        },
      ],
    };
  }, [nonInternalCompanies]);

  //  Time labels (include "Custom")
  const timeLabels = {
    "1w": "1 Week",
    "1m": "1 Month",
    "3m": "3 Months",
    "6m": "6 Months",
    "1y": "12 Months",
    "custom": "Custom Range",
  };

  //  Compute effective date range
  const { startDateForTrend, endDateForTrend } = useMemo(() => {
    const now = new Date();
    if (demoTimeRange === "custom") {
      const start = customStartDate ? new Date(customStartDate) : new Date(now.getFullYear() - 1, now.getMonth(), 1);
      const end = customEndDate ? new Date(customEndDate) : now;
      return { startDateForTrend: start, endDateForTrend: end };
    } else {
      let start;
      if (demoTimeRange === "1w") {
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      } else {
        const monthsBack = { "1m": 1, "3m": 3, "6m": 6, "1y": 12 }[demoTimeRange] || 6;
        start = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
      }
      return { startDateForTrend: start, endDateForTrend: now };
    }
  }, [demoTimeRange, customStartDate, customEndDate]);

  // === 3. Time Trend (Line Chart) with Custom Date Support ===
  const timeTrendData = useMemo(() => {
    const companiesWithDemoDate = nonInternalCompanies.filter(
      (c) => c.demoDate && parseCountField(c.demo) > 0
    );

    if (companiesWithDemoDate.length === 0) {
      return { labels: [], datasets: [], hasData: false };
    }

    const now = endDateForTrend;
    const startDate = startDateForTrend;

    // Generate monthly buckets between startDate and now
    const months = [];
    let current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    while (current <= now) {
      months.push(new Date(current));
      current.setMonth(current.getMonth() + 1);
    }

    const monthLabels = months.map((m) =>
      m.toLocaleString("default", { month: "short", year: "numeric" })
    );

    const monthlyCounts = {};
    monthLabels.forEach(label => {
      monthlyCounts[label] = 0;
    });

    companiesWithDemoDate.forEach((c) => {
      const demoDate = new Date(c.demoDate);
      if (isNaN(demoDate.getTime()) || demoDate < startDate || demoDate > now) return;

      for (let i = 0; i < months.length; i++) {
        const monthStart = new Date(months[i].getFullYear(), months[i].getMonth(), 1);
        const monthEnd = new Date(months[i].getFullYear(), months[i].getMonth() + 1, 0);
        if (demoDate >= monthStart && demoDate <= monthEnd) {
          const label = monthLabels[i];
          monthlyCounts[label] += parseCountField(c.demo);
          break;
        }
      }
    });

    return {
      labels: monthLabels,
      datasets: [
        {
          label: "Demos Completed",
          data: monthLabels.map((label) => monthlyCounts[label]),
          borderColor: "rgba(5, 55, 99, 1)",
          backgroundColor: "rgba(5, 55, 99, 0.1)",
          tension: 0.3,
          pointRadius: 3,
          pointBackgroundColor: "rgba(5, 55, 99, 1)",
        },
      ],
      hasData: true,
    };
  }, [nonInternalCompanies, startDateForTrend, endDateForTrend]);

  // === 4. Top Companies by Demo Count ===
  const topCompaniesData = useMemo(() => {
    const companiesWithDemos = nonInternalCompanies
      .map((c) => ({
        name: c.companyName || c._id || "Unnamed",
        demos: parseCountField(c.demo),
      }))
      .filter((c) => c.demos > 0)
      .sort((a, b) => b.demos - a.demos)
      .slice(0, 10);

    const formatCompanyName = (name = "") =>
      name
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());

    return {
      labels: companiesWithDemos.map((c) => {
        const formatted = formatCompanyName(c.name);
        return formatted.length > 20
          ? formatted.substring(0, 20) + "…"
          : formatted;
      }),
      datasets: [
        {
          label: "Demo Count",
          data: companiesWithDemos.map((c) => c.demos),
          backgroundColor: companiesWithDemos.map(
            (_, i) => `rgba(5, 55, 99, ${0.4 + (i / 10) * 0.6})`
          ),
          borderColor: "#fff",
          borderWidth: 1,
        },
      ],
    };
  }, [nonInternalCompanies]);

  // === Chart Options ===
  const doughnutOptions = { /* unchanged */ };
  const barOptions = { /* unchanged */ };
  const lineOptions = { /* unchanged */ };
  const horizontalBarOptions = { /* unchanged */ };

  const handleBack = () => {
    const dashboardPath = location.pathname.replace("/analytics", "/dashboard");
    navigate(dashboardPath);
  };

  const handleCompanyClick = (index) => {
    navigate(`/dashboard/companies/totalDemoCompanies`);
  };

  //  Time filter options (with "Custom")
  const timeFilterOptions = [
    { value: "1w", label: "1 Week" },
    { value: "1m", label: "1 Month" },
    { value: "3m", label: "3 Months" },
    { value: "6m", label: "6 Months" },
    { value: "1y", label: "1 Year" },
    { value: "custom", label: "Custom" },
  ];

  return (
    <div className="demo-analytics-container">
      {/* <div className="demo-header-section">
        <button className="back-btn" onClick={handleBack}>
          <FiArrowLeft size={16} /> Back
        </button>
        <h1 className="title">Demo Analytics</h1>
        <div className="demo-time-filter" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <select
            value={demoTimeRange}
            onChange={(e) => setDemoTimeRange(e.target.value)}
            style={{
              padding: '8px 14px',
              borderRadius: '10px',
              fontSize: '14px',
              background: 'white',
              border: '1px solid #cbd5e1',
              color: '#1e293b',
              cursor: 'pointer',
              minWidth: '110px',
            }}
          >
            {timeFilterOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {demoTimeRange === "custom" && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                style={{
                  padding: '6px',
                  fontSize: '13px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                }}
              />
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                style={{
                  padding: '6px',
                  fontSize: '13px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                }}
              />
            </div>
          )}
        </div>
      </div> */}

      {loading ? (
        <div className="demo-loading">Loading demo data...</div>
      ) : (
        <div className="demo-analytics-content">
          <div className="demo-chart-panel" style={{ gridArea: 'geo' }}>
            <h3>Demos by Geography</h3>
            <p className="demo-chart-subtitle">
              Total demos across {nonInternalCompanies.length} companies (non-internal)
            </p>
            <div className="demo-chart-wrapper">
              <Doughnut data={geographyData} options={doughnutOptions} />
            </div>
          </div>

          <div className="demo-chart-panel-top" style={{ gridArea: 'top' }}>
            <h3>Top Companies by Demo Count</h3>
            <p className="demo-chart-subtitle">
              Highest-engagement accounts by demo volume
            </p>
            <div className="demo-chart-wrapper">
              <Bar data={topCompaniesData} options={horizontalBarOptions} />
            </div>
          </div>

          <div className="demo-chart-panel" style={{ gridArea: 'trend' }}>
            <h3>Recent Demo Activity (Last {timeLabels[demoTimeRange]})</h3>
            <p className="demo-chart-subtitle">
              Based on companies with recorded demo dates
            </p>
            <div className="demo-chart-wrapper">
              {timeTrendData.hasData ? (
                <Line data={timeTrendData} options={lineOptions} />
              ) : (
                <div className="demo-no-data">No demo dates recorded</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}